import { ReactNode, useEffect, useState } from "react";
import { useSearchResultItems } from "@made-people/grease-search";
import {
  CentraSearchResultsProvider,
  CentraSearchResultsProviderProps,
} from "@made-people/grease-centra";
import { Product } from "@made-people/grease-product";

export const QuickShopWithSiblings = ({
  initialPayload,
  children,
}: {
  initialPayload: CentraSearchResultsProviderProps["payload"];
  children: (products: Product[]) => ReactNode;
}) => {
  return (
    <CentraSearchResultsProvider
      payload={{
        ...initialPayload,
        limit: 1,
      }}
    >
      <InitialProductLoader initialPayload={initialPayload}>
        {children}
      </InitialProductLoader>
    </CentraSearchResultsProvider>
  );
};

const InitialProductLoader = ({
  initialPayload,
  children,
}: {
  initialPayload: CentraSearchResultsProviderProps["payload"];
  children: (products: Product[]) => ReactNode;
}) => {
  const [allProductIds, setAllProductIds] = useState<string[] | null>(null);
  const items = useSearchResultItems();

  useEffect(() => {
    if (items && items.length > 0 && items[0].type === "product") {
      const product = items[0].product;
      const siblingProducts = product.siblingProducts || [];

      const siblingIds = siblingProducts
        .map((sibling) => sibling.productId)
        .filter((id) => id !== product.base.productId);

      const allIds = [product.base.productId, ...siblingIds];

      setAllProductIds(allIds);
    }
  }, [items]);

  if (!allProductIds) {
    return <div className="w-full h-96 animate-pulse bg-gray-200" />;
  }

  return (
    <CentraSearchResultsProvider
      payload={{
        ...initialPayload,
        products: allProductIds,
        limit: allProductIds.length,
      }}
    >
      <QuickShopProductsLoader>{children}</QuickShopProductsLoader>
    </CentraSearchResultsProvider>
  );
};

const QuickShopProductsLoader = ({
  children,
}: {
  children: (products: Product[]) => ReactNode;
}) => {
  const items = useSearchResultItems();

  if (!items || items.length === 0) {
    return <div className="w-full h-96 animate-pulse bg-gray-200" />;
  }

  const products = items
    .filter((item) => item.type === "product")
    .map((item) => (item as { type: "product"; product: Product }).product);

  return <>{children(products)}</>;
};
